import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Package, Calendar, Grid, Image as ImageIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import {
  useGetCategoryQuery,
  useDeleteCategoryMutation,
} from '../../app/api';

const CategoryView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ✅ RTK Query — auto cache
  const { data, isLoading } = useGetCategoryQuery(id);
  const [deleteCategory] = useDeleteCategoryMutation();

  const category = data?.data?.category;
  const imageBaseUrl = data?.data?.imageBaseUrl || '';

  const getFullImage = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return `${imageBaseUrl}${imagePath}`;
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await deleteCategory(id).unwrap();
      toast.success('Category deleted successfully');
      navigate('/categories');
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to delete');
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex-center" style={{ height: '60vh' }}>
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  if (!category) {
    return (
      <Layout>
        <div className="flex-center" style={{ height: '60vh' }}>
          <div>Category not found</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="view-category-page">
        <div className="page-header">
          <div>
            <Link
              to="/categories"
              className="btn btn-sm btn-outline btn-back"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}
            >
              <ArrowLeft size={16} />
              <span>Back to Categories</span>
            </Link>
            <h1 className="page-title">{category.name}</h1>
            <p className="page-subtitle">Category Details</p>
          </div>
          <div className="page-actions">
            <button className="btn btn-danger" onClick={handleDelete}>
              <Trash2 size={18} /> Delete
            </button>
            <Link to={`/categories/edit/${category._id}`} className="btn btn-primary">
              <Edit size={18} /> Edit Category
            </Link>
          </div>
        </div>

        <div className="grid grid-2 gap-lg">
          <div className="card">
            {category.image ? (
              <div className="category-view-image">
                <img src={getFullImage(category.image)} alt={category.name} className="category-detail-image" />
              </div>
            ) : (
              <div className="category-view-image placeholder">
                <ImageIcon size={48} />
                <span>No Image</span>
              </div>
            )}
            <div className="category-view-info">
              <div className="detail-row">
                <span className="detail-label">Name</span>
                <span className="detail-value">{category.name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Slug</span>
                <span className="detail-value">/{category.slug}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status</span>
                <span className="detail-value">
                  <span className={`badge ${category.status === 'Active' ? 'badge-success' : 'badge-danger'}`}>
                    {category.status}
                  </span>
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Products</span>
                <span className="detail-value">{category.products || 0} products</span>
              </div>
            </div>
          </div>

          <div>
            <div className="card mb-lg">
              <h3 className="card-title">Description</h3>
              <p className="text-muted">{category.description || 'No description available'}</p>
            </div>

            <div className="card">
              <h3 className="card-title">Category Information</h3>
              <div className="product-meta">
                <div className="meta-item">
                  <Grid size={16} className="meta-icon" />
                  <div>
                    <div className="meta-label">Category ID</div>
                    <div className="meta-value">#{category._id}</div>
                  </div>
                </div>
                <div className="meta-item">
                  <Package size={16} className="meta-icon" />
                  <div>
                    <div className="meta-label">Total Products</div>
                    <div className="meta-value">{category.products || 0}</div>
                  </div>
                </div>
                <div className="meta-item">
                  <Calendar size={16} className="meta-icon" />
                  <div>
                    <div className="meta-label">Created</div>
                    <div className="meta-value">
                      {new Date(category.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="meta-item">
                  <Calendar size={16} className="meta-icon" />
                  <div>
                    <div className="meta-label">Last Updated</div>
                    <div className="meta-value">
                      {new Date(category.updatedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CategoryView;